<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card sales-pos-card">
          <div class="card-body sales-pos-body">
            <div class="sales-pos-shell">
              <section class="customer-strip">
                <div class="customer-strip-head">
                  <div>
                    <h4 class="strip-title">Customer & Bill</h4>
                    <p class="strip-subtitle">Keep the operator on one line. Search, confirm, bill.</p>
                  </div>
                  <button
                    type="button"
                    class="customer-more-toggle"
                    @click="moreCustomerDetailsOpen = !moreCustomerDetailsOpen"
                  >
                    {{ moreCustomerDetailsOpen ? "Hide Details" : "More Details" }}
                  </button>
                </div>

                <div class="customer-strip-grid">
                  <div class="customer-main-field">
                    <label class="compact-label">Customer Search</label>
                    <div class="lookup-control-row">
                      <div class="lookup-box">
                        <b-form-input
                          v-model="customerSearchQuery"
                          type="text"
                          autocomplete="off"
                          class="compact-input"
                          placeholder="Search customer by name or mobile"
                          @focus="showCustomerResults = true"
                          @input="onCustomerLookupInput"
                        />
                        <div v-if="showCustomerResults" class="lookup-results">
                          <button
                            v-for="customer in customerSearchResults"
                            :key="customer.customerid"
                            type="button"
                            class="lookup-result-item"
                            @mousedown.prevent="selectCustomer(customer)"
                          >
                            <span class="lookup-result-title">{{ customer.customername || "Unnamed customer" }}</span>
                            <span class="lookup-result-meta">{{ customer.mobile || "No mobile" }}</span>
                          </button>
                          <div
                            v-if="customerSearchQuery.trim() && !customerSearchResults.length"
                            class="lookup-empty-state"
                          >
                            No customer found. Use <strong>Add New</strong>.
                          </div>
                        </div>
                      </div>
                      <button
                        v-if="canAddCustomers"
                        type="button"
                        class="inline-add-btn"
                        title="Add new customer"
                        @click="openCustomerModal"
                      >
                        <i class="fas fa-user-plus"></i>
                        <span>Add New</span>
                      </button>
                    </div>
                  </div>

                  <div class="compact-field">
                    <label class="compact-label">Mobile</label>
                    <InternationalPhoneInput
                      ref="saleMobileInput"
                      v-model="form.mobile"
                      :submitted="salePhoneSubmitted"
                      input-class="compact-input"
                      placeholder="Mobile number"
                    />
                  </div>

                  <div class="compact-field">
                    <label class="compact-label">Date</label>
                    <b-form-input
                      v-model="form.billdate"
                      type="date"
                      class="compact-input"
                    />
                  </div>
                  <div class="compact-field">
                    <label class="compact-label">Bill No</label>
                    <b-form-input
                      v-model="form.billno"
                      type="text"
                      class="compact-input"
                      readonly
                    />
                  </div>
                </div>

                <div v-if="moreCustomerDetailsOpen" class="customer-more-grid">
                  <div class="compact-field">
                    <label class="compact-label">Customer Name</label>
                    <b-form-input
                      v-model="form.customername"
                      type="text"
                      class="compact-input"
                      placeholder="Customer name"
                    />
                  </div>
                  <div class="compact-field">
                    <label class="compact-label">GST No</label>
                    <b-form-input
                      v-model="form.gstno"
                      type="text"
                      class="compact-input"
                      placeholder="GST number"
                    />
                  </div>
                  <div class="compact-field customer-address-field">
                    <label class="compact-label">Address</label>
                    <b-form-textarea
                      v-model="form.address"
                      rows="2"
                      class="compact-textarea"
                      placeholder="Address"
                    />
                  </div>
                </div>
              </section>

              <div v-if="showInterstateIndicator" class="tax-state-row">
                <span class="interstate-badge" :class="isInterState ? 'interstate-igst' : 'interstate-intra'">
                  {{ isInterState ? "Inter-State Billing (IGST)" : "Intra-State Billing (CGST + SGST)" }}
                </span>
              </div>

              <section class="items-panel">
                <div class="items-panel-head">
                  <div>
                    <h4 class="strip-title">Item Entry</h4>
                    <p class="strip-subtitle">Search item, confirm quantity, move to next row.</p>
                  </div>
                  <div class="items-head-actions">
                    <span class="active-row-badge">Active Row {{ activeRow + 1 }} / {{ form.items.length }}</span>
                    <button type="button" class="primary-row-btn" @click="addItemAndFocus">+ Add Item</button>
                  </div>
                </div>

                <div class="pos-table-wrap">
                  <table class="pos-table">
                    <thead>
                      <tr>
                        <th class="col-num">#</th>
                        <th class="col-item">Item Search</th>
                        <th class="col-layer">Batch / Layer</th>
                        <th class="col-qty">Qty</th>
                        <th class="col-rate">Rate</th>
                        <th class="col-disc">Disc %</th>
                        <th class="col-gst">GST %</th>
                        <th class="col-taxable">Taxable</th>
                        <th class="col-total">Total</th>
                        <th class="col-remove"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, index) in form.items"
                        :key="index"
                        class="pos-row"
                        :class="{ 'pos-row-active': activeRow === index }"
                        @mousedown="setActiveRow(index)"
                      >
                        <td class="cell-num">{{ index + 1 }}</td>

                        <td class="cell-item">
                          <div class="item-search-cell">
                            <div
                              ref="itemTrigger"
                              class="item-dd-trigger item-dd-trigger-large"
                              tabindex="0"
                              role="button"
                              @click="toggleDropdown(index)"
                              @keydown.enter.prevent="toggleDropdown(index)"
                            >
                              <span :class="item.itemname ? 'item-dd-selected' : 'item-dd-placeholder'">
                                {{ item.itemname || "Scan barcode or search item" }}
                              </span>
                              <span class="item-dd-arrow">▼</span>
                            </div>

                            <div
                              v-if="openDropdownIndex === index"
                              class="item-dd-portal"
                              :style="portalStyle"
                            >
                              <div class="item-dd-search-wrap">
                                <input
                                  v-model="searchText"
                                  type="text"
                                  autocomplete="off"
                                  class="item-dd-search"
                                  placeholder="Type item name / item code / barcode"
                                  @input="onSearchInput"
                                  @keydown.enter.prevent="selectFirstFilteredItem(index)"
                                  @keydown.esc.prevent="closeDropdown"
                                />
                              </div>
                              <div class="item-dd-list">
                                <div v-if="searchLoading" class="item-dd-msg">Searching...</div>
                                <div
                                  v-else-if="searchText.trim() && !filteredItems.length"
                                  class="item-dd-msg"
                                >
                                  No items found
                                </div>
                                <div
                                  v-else-if="!searchText.trim() && !filteredItems.length"
                                  class="item-dd-msg"
                                >
                                  Start typing to search items
                                </div>
                                <button
                                  v-for="opt in filteredItems"
                                  :key="opt.itemid"
                                  type="button"
                                  class="item-dd-option"
                                  :class="{ 'item-dd-option-active': item.itemid === opt.itemid }"
                                  @mousedown.prevent="selectItem(opt, index)"
                                >
                                  <span class="item-option-name">{{ opt.itemname }}</span>
                                  <span class="item-option-meta">
                                    {{ opt.barcode || opt.itemcode || "Code unavailable" }}
                                  </span>
                                </button>
                              </div>
                            </div>

                            <div class="row-submeta">
                              <span v-if="item.itemcategory" class="row-meta-pill row-meta-soft">Category linked</span>
                              <span v-if="item.iteminfo?.available || item.layerRemainingQty" class="row-meta-pill">
                                Stock {{ formatNumber(item.layerRemainingQty || item.iteminfo?.available || 0) }}
                              </span>
                              <span v-if="item.purchaseprice" class="row-meta-pill row-meta-muted">
                                Cost {{ formatCurrency(item.purchaseprice) }}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td class="cell-layer">
                          <b-form-select
                            v-model="item.selectedPurchaseItemId"
                            :options="item.layerOptions"
                            :disabled="!item.layerOptions.length"
                            class="table-select"
                            @change="onLayerChange(item, $event)"
                          />
                          <div class="row-submeta row-submeta-tight">
                            <span class="row-meta-pill" :class="stockBadgeClass(item)">
                              {{ stockStatusLabel(item) }}
                            </span>
                            <span v-if="item.batchno" class="row-meta-pill row-meta-muted">Batch {{ item.batchno }}</span>
                            <span v-if="item.expirydate" class="row-meta-pill row-meta-muted">Exp {{ formatDate(item.expirydate) }}</span>
                          </div>
                        </td>

                        <td class="cell-qty">
                          <b-form-input
                            ref="qtyInput"
                            v-model.number="item.quantity"
                            type="number"
                            min="0"
                            :max="item.layerRemainingQty || (item.iteminfo && item.iteminfo.available) || undefined"
                            class="table-input qty-input"
                            @input="calculateItemTotal(item)"
                            @keyup.enter="handleQtyEnter(index)"
                          />
                          <div class="qty-inline-note">
                            {{ stockNote(item) }}
                          </div>
                        </td>

                        <td class="cell-rate">
                          <b-form-input
                            v-model.number="item.rate"
                            type="number"
                            min="0"
                            step="0.01"
                            class="table-input"
                            @input="calculateItemTotal(item)"
                          />
                          <div class="row-submeta row-submeta-tight">
                            <span class="row-meta-pill row-meta-muted">
                              {{ item.istaxinclusive ? "GST Inclusive" : "GST Extra" }}
                            </span>
                          </div>
                        </td>

                        <td class="cell-disc">
                          <b-form-input
                            v-model.number="item.discountamt"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            class="table-input"
                            @input="calculateItemTotal(item)"
                          />
                          <div class="qty-inline-note">
                            {{ formatCurrency(item.discountvalue || 0) }}
                          </div>
                        </td>

                        <td class="cell-gst">
                          <b-form-input
                            v-model.number="item.taxpercent"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            class="table-input"
                            :disabled="item.taxtype === 'EXEMPT' || item.taxtype === 'NONGST'"
                            @input="calculateItemTotal(item)"
                          />
                          <div class="row-submeta row-submeta-tight">
                            <span class="row-meta-pill row-meta-soft">{{ item.taxtype || "GST" }}</span>
                            <span class="row-meta-pill row-meta-muted">
                              {{ item.istaxinclusive ? "Incl" : "Excl" }}
                            </span>
                          </div>
                        </td>

                        <td class="cell-taxable">
                          <div class="value-display">{{ formatCurrency(item.taxablevalue) }}</div>
                          <div class="qty-inline-note">
                            Tax {{ formatCurrency(item.taxamount) }}
                          </div>
                        </td>

                        <td class="cell-total">
                          <div class="value-display value-display-strong">{{ formatCurrency(lineGrandTotal(item)) }}</div>
                          <div class="qty-inline-note">
                            {{ isInterState ? "IGST" : "CGST/SGST" }} {{ formatCurrency(item.taxamount) }}
                          </div>
                        </td>

                        <td class="cell-remove">
                          <button
                            v-if="form.items.length > 1 || item.itemid"
                            type="button"
                            class="remove-row-btn"
                            :title="form.items.length > 1 ? 'Remove row' : 'Clear selected item'"
                            @click="removeItem(index)"
                          >
                            <i class="fas fa-times"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section class="sales-bottom-grid">
                <div class="notes-panel">
                  <div class="notes-card">
                    <div class="notes-card-head">
                      <div>
                        <h4 class="strip-title">Remarks</h4>
                        <p class="strip-subtitle">Keep special instructions or payment notes here.</p>
                      </div>
                      <span v-if="hasInclusiveItems" class="notes-hint-badge">Includes MRP tax breakup</span>
                    </div>
                    <b-form-textarea
                      v-model="form.remarks"
                      rows="3"
                      class="compact-textarea notes-textarea"
                      placeholder="Optional billing remarks"
                    />
                  </div>
                </div>

                <aside class="totals-panel">
                  <div class="totals-card">
                    <div class="totals-card-head">
                      <div>
                        <h4 class="strip-title">Bill Summary</h4>
                        <p class="strip-subtitle">Compact tax summary for final confirmation.</p>
                      </div>
                      <span class="summary-count-pill">{{ form.items.length }} Items</span>
                    </div>

                    <div class="totals-row totals-payment-row">
                      <span>Payment Mode</span>
                      <b-form-select
                        v-model="form.paymentmode"
                        :options="paymentModeOptions"
                        class="totals-select"
                      />
                    </div>
                    <div class="totals-row">
                      <span>Subtotal</span>
                      <strong>{{ formatCurrency(totals.taxableValue) }}</strong>
                    </div>
                    <div class="totals-row">
                      <span>Discount</span>
                      <strong class="text-danger">- {{ formatCurrency(totals.totalDiscount) }}</strong>
                    </div>
                    <div class="totals-row" v-if="!isInterState">
                      <span>CGST</span>
                      <strong>{{ formatCurrency(totals.cgst) }}</strong>
                    </div>
                    <div class="totals-row" v-if="!isInterState">
                      <span>SGST</span>
                      <strong>{{ formatCurrency(totals.sgst) }}</strong>
                    </div>
                    <div class="totals-row" v-if="isInterState || totals.igst > 0">
                      <span>IGST</span>
                      <strong>{{ formatCurrency(totals.igst) }}</strong>
                    </div>
                    <div class="totals-row">
                      <span>Total Tax</span>
                      <strong>{{ formatCurrency(totals.totalTax) }}</strong>
                    </div>
                    <div class="totals-row">
                      <span>Roundoff</span>
                      <b-form-input
                        v-model.number="form.roundoff"
                        type="number"
                        step="0.01"
                        class="totals-input"
                        @input="computeTotals"
                      />
                    </div>
                    <div class="totals-row totals-grand-row">
                      <span>Grand Total</span>
                      <strong>{{ formatCurrency(totals.netAmount) }}</strong>
                    </div>
                    <div v-if="isCreditSale" class="credit-payment-box">
                      <div class="totals-row">
                        <span>Paid Now</span>
                        <b-form-input
                          v-model.number="form.paid_amount"
                          type="number"
                          min="0"
                          :max="totals.netAmount"
                          step="0.01"
                          class="totals-input"
                        />
                      </div>
                      <div class="totals-row">
                        <span>Due Date</span>
                        <b-form-input
                          v-model="form.due_date"
                          type="date"
                          class="totals-input"
                        />
                      </div>
                      <div class="totals-row">
                        <span>Pending</span>
                        <strong class="text-warning">{{ formatCurrency(pendingCreditAmount) }}</strong>
                      </div>
                      <div class="totals-row">
                        <span>Status</span>
                        <strong class="credit-status-chip">{{ paymentStatusLabel }}</strong>
                      </div>
                    </div>
                  </div>
                </aside>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sticky-bill-footer">
      <div class="sticky-footer-summary">
        <span class="sticky-footer-label">Net Payable</span>
        <strong class="sticky-footer-total">{{ formatCurrency(totals.netAmount) }}</strong>
        <span class="sticky-footer-meta">{{ form.items.length }} rows | {{ form.paymentmode }}<template v-if="isCreditSale"> | Pending {{ formatCurrency(pendingCreditAmount) }}</template></span>
      </div>
      <div class="sticky-footer-actions">
        <button type="button" class="footer-btn footer-btn-light" @click="clearForm">Clear</button>
        <router-link to="/saleslist" class="footer-btn footer-btn-muted">Cancel</router-link>
        <button type="button" class="footer-btn footer-btn-primary" @click="submitSales">Save Bill</button>
      </div>
    </div>

    <div
      v-if="showCustomerModal"
      class="quick-create-modal-backdrop"
      @click.self="closeCustomerModal"
    >
      <div class="quick-create-modal" role="dialog" aria-modal="true" aria-labelledby="customer-quick-create-title">
        <div class="quick-create-modal-header">
          <div>
            <h5 id="customer-quick-create-title" class="quick-create-modal-title">Add New Customer</h5>
            <p class="quick-create-modal-subtitle">Customer ID will be created automatically.</p>
          </div>
          <button type="button" class="quick-create-close" aria-label="Close" @click="closeCustomerModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="compact-label">Customer Name</label>
            <b-form-input
              ref="customerModalNameInput"
              v-model="customerModalForm.customername"
              type="text"
              class="compact-input"
              placeholder="Enter customer name"
            />
          </div>
          <div class="col-md-6">
            <label class="compact-label">Mobile</label>
            <InternationalPhoneInput
              ref="customerModalMobileInput"
              v-model="customerModalForm.mobile"
              :submitted="customerModalPhoneSubmitted"
              input-class="compact-input"
              placeholder="Enter mobile number"
            />
          </div>
          <div class="col-md-6">
            <label class="compact-label">GST No</label>
            <b-form-input
              v-model="customerModalForm.gstno"
              type="text"
              class="compact-input"
              placeholder="Enter GST number"
            />
          </div>
          <div class="col-12">
            <label class="compact-label">Address</label>
            <b-form-textarea
              v-model="customerModalForm.address"
              rows="3"
              class="compact-textarea"
              placeholder="Enter customer address"
            />
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn btn-light" @click="closeCustomerModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="customerModalSaving" @click="createCustomerFromModal">
            {{ customerModalSaving ? "Saving..." : "Save Customer" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import InternationalPhoneInput from "@/components/InternationalPhoneInput.vue";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";

export default {
  name: "add-sales",
  components: { InternationalPhoneInput },
  data() {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return {
      title: "Sales",
      title1: "Create New Sale",
      selectedCustomer: "",
      customerSearchQuery: "",
      quickItemSearch: "",
      showCustomerResults: false,
      customerLookupTimer: null,
      showCustomerModal: false,
      customerModalSaving: false,
      moreCustomerDetailsOpen: false,
      activeRow: 0,
      paymentModeOptions: [
        { value: "Cash", text: "Cash" },
        { value: "Card", text: "Card" },
        { value: "UPI", text: "UPI" },
        { value: "Bank Transfer", text: "Bank Transfer" },
        { value: "Credit", text: "Credit" },
      ],
      categoriesFromApi: [],
      customerListFromApi: [],
      allItems: [],
      filteredItems: [],
      openDropdownIndex: null,
      searchText: "",
      searchLoading: false,
      portalStyle: {},
      _searchTimer: null,
      form: {
        billno: "",
        billdate: todayStr,
        customerid: "",
        customername: "",
        mobile: "",
        address: "",
        gstno: "",
        paymentmode: "Cash",
        paid_amount: 0,
        due_date: "",
        roundoff: 0,
        remarks: "",
        items: [],
      },
      customerModalForm: {
        customername: "",
        mobile: "",
        gstno: "",
        address: "",
      },
      salePhoneSubmitted: false,
      customerModalPhoneSubmitted: false,
      totals: {
        totalAmount: 0,
        taxableValue: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalDiscount: 0,
        totalTax: 0,
        netAmount: 0,
      },
      shopStateCode: null,
    };
  },
  computed: {
    canAddCustomers() {
      return this.$store.getters.hasPermission("customer.add");
    },
    isInterState() {
      if (!this.form.gstno || !this.shopStateCode) return false;
      const customerState = `${this.form.gstno}`.substring(0, 2);
      return customerState !== this.shopStateCode;
    },
    customerSearchResults() {
      return this.getRankedLookupResults(this.customerListFromApi, this.customerSearchQuery, [
        "customername",
        "mobile",
      ]).slice(0, 8);
    },
    showInterstateIndicator() {
      return Boolean(this.form.gstno && this.shopStateCode);
    },
    locationOptions() {
      return (this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      }));
    },
    hasInclusiveItems() {
      return this.form.items.some((item) => Boolean(item.istaxinclusive));
    },
    isCreditSale() {
      return this.form.paymentmode === "Credit";
    },
    pendingCreditAmount() {
      if (!this.isCreditSale) return 0;
      return Math.max(0, Number((this.totals.netAmount - (parseFloat(this.form.paid_amount) || 0)).toFixed(2)));
    },
    paymentStatusLabel() {
      if (!this.isCreditSale) return "PAID";
      const paidAmount = parseFloat(this.form.paid_amount) || 0;
      if (paidAmount <= 0) return "UNPAID";
      if (paidAmount >= this.totals.netAmount) return "PAID";
      return "PARTIAL";
    },
  },
  watch: {
    "form.paymentmode"(value) {
      if (value !== "Credit") {
        this.form.paid_amount = 0;
        this.form.due_date = "";
      }
    },
    "form.billdate"() {
      this.fetchNextBillNo();
    },
    selectedCustomer(newCustomerId) {
      const customer = this.customerListFromApi.find((c) => c.customerid === newCustomerId);
      this.form.customerid = customer?.customerid || "";
      this.form.customername = customer?.customername || "";
      this.form.mobile = customer?.mobile || "";
      this.form.address = customer?.address || "";
      this.form.gstno = customer?.gstno || "";
      this.customerSearchQuery = customer ? this.buildCustomerLabel(customer) : "";
      this.showCustomerResults = false;
      this.$nextTick(() => this.form.items.forEach((item) => this.calculateItemTotal(item)));
    },
    "form.locationid": {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue) return;
        const rows = this.form.items.filter((item) => item.itemid);
        await Promise.all(rows.map((item) => this.fetchPurchaseLayers(item)));
      },
    },
  },
  created() {
    this.form = this.getInitialForm();
    this.fetchCategoryListFromApi();
    this.fetchShopStateCode();
    this.fetchNextBillNo();
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("mousedown", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("mousedown", this.handleOutsideClick);
    clearTimeout(this._searchTimer);
    clearTimeout(this.customerLookupTimer);
  },
  methods: {
    async fetchNextBillNo() {
      if (!this.form.billdate) return;
      try {
        const response = await axios.get("/v1/sales/next-billno", {
          params: { billdate: this.form.billdate },
        });
        this.form.billno = response.data?.billno || "";
      } catch (error) {
        console.error("Error fetching next sales bill number:", error);
      }
    },

    handleOutsideClick(e) {
      const customerLookup = this.$el?.querySelector(".lookup-box");
      if (customerLookup && !customerLookup.contains(e.target)) {
        this.showCustomerResults = false;
      }

      if (this.openDropdownIndex === null) return;
      const portal = document.querySelector(".item-dd-portal");
      const wraps = document.querySelectorAll(".item-search-cell");
      let inside = false;
      if (portal && portal.contains(e.target)) inside = true;
      wraps.forEach((wrap) => {
        if (wrap.contains(e.target)) inside = true;
      });
      if (!inside) this.closeDropdown();
    },

    handleKeydown(e) {
      if (e.ctrlKey && e.key.toLowerCase() === "m") {
        e.preventDefault();
        this.addItemAndFocus();
        return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        this.submitSales();
        return;
      }
      if (e.key === "Escape") {
        if (this.showCustomerModal) {
          this.closeCustomerModal();
          return;
        }
        this.showCustomerResults = false;
        this.closeDropdown();
      }
    },

    setActiveRow(index) {
      this.activeRow = index;
    },

    focusQtyInput(index) {
      const target = Array.isArray(this.$refs.qtyInput) ? this.$refs.qtyInput[index] : this.$refs.qtyInput;
      if (target?.focus) {
        target.focus();
        return;
      }
      const input = target?.$el?.querySelector("input");
      if (input) input.focus();
    },

    async focusRowItemSearch(index) {
      this.setActiveRow(index);
      await this.toggleDropdown(index);
    },

    handleQtyEnter(index) {
      const nextIndex = index + 1;
      if (nextIndex >= this.form.items.length) {
        this.addItem({ focus: true });
        return;
      }
      this.focusRowItemSearch(nextIndex);
    },

    lineGrandTotal(item) {
      const amount = parseFloat(item.amount || 0);
      const tax = parseFloat(item.taxamount || 0);
      return item.istaxinclusive ? amount : amount + tax;
    },

    formatNumber(value) {
      return parseFloat(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      });
    },

    stockStatusLabel(item) {
      const stock = parseFloat(item.layerRemainingQty || item.iteminfo?.available || 0);
      if (stock <= 0) return "Out of stock";
      if (stock <= 5) return `Low stock ${this.formatNumber(stock)}`;
      return `Stock ${this.formatNumber(stock)}`;
    },

    stockBadgeClass(item) {
      const stock = parseFloat(item.layerRemainingQty || item.iteminfo?.available || 0);
      if (stock <= 0) return "row-meta-danger";
      if (stock <= 5) return "row-meta-warning";
      return "row-meta-success";
    },

    stockNote(item) {
      const stock = parseFloat(item.layerRemainingQty || item.iteminfo?.available || 0);
      if (!item.itemid) return "Pick item first";
      if (stock <= 0) return "No stock available";
      return `Available ${this.formatNumber(stock)}`;
    },

    normalizeLookupValue(value) {
      return `${value ?? ""}`.trim().toLowerCase();
    },

    getRankedLookupResults(list, query, fields, sortField = fields[0]) {
      const normalizedQuery = this.normalizeLookupValue(query);
      if (!normalizedQuery) return list.slice(0, 8);

      return list
        .map((entry) => {
          const values = fields
            .map((field) => this.normalizeLookupValue(entry[field]))
            .filter(Boolean);

          let score = -1;
          if (values.some((value) => value === normalizedQuery)) score = 0;
          else if (values.some((value) => value.startsWith(normalizedQuery))) score = 1;
          else if (values.some((value) => value.includes(normalizedQuery))) score = 2;

          return { entry, score };
        })
        .filter((item) => item.score >= 0)
        .sort((a, b) => {
          if (a.score !== b.score) return a.score - b.score;
          return this.normalizeLookupValue(a.entry[sortField]).localeCompare(
            this.normalizeLookupValue(b.entry[sortField])
          );
        })
        .map((item) => item.entry);
    },

    rankItemResults(list, query) {
      return this.getRankedLookupResults(list, query, ["itemname", "itemcode", "barcode"], "itemname");
    },

    async toggleDropdown(index) {
      if (this.openDropdownIndex === index) {
        this.closeDropdown();
        return;
      }

      this.setActiveRow(index);
      this.openDropdownIndex = index;
      this.searchText = "";
      this.filteredItems = [];
      this.allItems = [];
      this.fetchItemListFromApi("", "");

      this.$nextTick(() => {
        const triggers = this.$refs.itemTrigger || [];
        const trigger = Array.isArray(triggers) ? triggers[index] : triggers;
        const element = trigger?.$el || trigger;
        if (!element?.getBoundingClientRect) return;
        const rect = element.getBoundingClientRect();
        this.portalStyle = {
          position: "fixed",
          top: `${rect.bottom + 2}px`,
          left: `${rect.left}px`,
          width: `${Math.max(rect.width, 360)}px`,
          zIndex: "999999",
        };
        this.$nextTick(() => {
          const input = document.querySelector(".item-dd-search");
          if (input) input.focus();
        });
      });
    },

    closeDropdown() {
      this.openDropdownIndex = null;
      this.searchText = "";
      this.searchLoading = false;
    },

    async onSearchInput() {
      const query = this.searchText.trim();
      clearTimeout(this._searchTimer);

      if (!query) {
        await this.fetchItemListFromApi("", "");
        return;
      }

      this.searchLoading = true;
      this._searchTimer = setTimeout(async () => {
        try {
          await this.fetchItemListFromApi("", query);
          this.filteredItems = this.rankItemResults(this.filteredItems, query);
        } catch (error) {
          console.error("Search error:", error);
        } finally {
          this.searchLoading = false;
        }
      }, 180);
    },

    async selectFirstFilteredItem(index) {
      if (!this.filteredItems.length) return;
      await this.selectItem(this.filteredItems[0], index);
    },

    async applyQuickItemSearch() {
      const query = this.quickItemSearch.trim();
      const rowIndex = this.activeRow ?? 0;
      if (!query) {
        this.focusRowItemSearch(rowIndex);
        return;
      }

      try {
        const params = { search: query };
        params.stocked_only = true;
        const res = await axios.get("/v1/sales/items", { params });
        const items = Array.isArray(res.data) ? res.data : res.data.items ?? [];
        const ranked = this.rankItemResults(items, query);
        const match = ranked[0] || items[0];
        if (!match) {
          Swal.fire({ icon: "info", title: "No matching item found" });
          return;
        }
        await this.selectItem(match, rowIndex);
        this.quickItemSearch = "";
      } catch (error) {
        console.error("Quick search error:", error);
        Swal.fire({ icon: "error", title: "Unable to search item" });
      }
    },

    async selectItem(opt, index) {
      const item = this.form.items[index];
      const previousItemId = item.itemid;
      try {
        item.itemid = opt.itemid;
        item.itemname = opt.itemname;
        item.itemcategory = opt.itemcategory ?? opt.categoryid ?? item.itemcategory ?? "";
        await this.fetchPurchaseLayers(item);
        this.refreshLayerOptionsForItem(previousItemId);
      } catch (error) {
        console.error("Error fetching item details:", error);
      }
      this.closeDropdown();
      this.setActiveRow(index);
      this.$nextTick(() => this.focusQtyInput(index));
    },

    newItemRow() {
      return {
        itemcategory: "",
        itemid: "",
        itemname: "",
        rate: 0,
        purchaseprice: 0,
        discountamt: 0,
        discountvalue: 0,
        quantity: 0,
        freequantity: 0,
        amount: 0,
        taxtype: "GST",
        taxpercent: 0,
        taxablevalue: 0,
        taxamount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        istaxinclusive: false,
        batchno: "",
        expirydate: "",
        iteminfo: null,
        _lastLoadedItemId: "",
        _layerRequestId: 0,
        selectedPurchaseItemId: null,
        layerOptions: [],
        allLayers: [],
        layerMap: {},
        layerRemainingQty: 0,
      };
    },

    getToday() {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },

    getInitialForm() {
      return {
        billno: "",
        billdate: this.getToday(),
        locationid: this.$store.getters.getCurrentLocation?.locationid || "",
        customerid: "",
        customername: "",
        mobile: "",
        address: "",
        gstno: "",
        paymentmode: "Cash",
        paid_amount: 0,
        due_date: "",
        roundoff: 0,
        remarks: "",
        items: [this.newItemRow()],
      };
    },

    getEmptyCustomerModalForm() {
      return {
        customername: "",
        mobile: "",
        gstno: "",
        address: "",
      };
    },

    buildCustomerLabel(customer) {
      const parts = [customer.customername || "Unnamed customer"];
      if (customer.mobile) parts.push(customer.mobile);
      return parts.join(" | ");
    },

    getLocationId() {
      return this.form.locationid || this.$store.getters.getCurrentLocation?.locationid || "";
    },

    onCustomerLookupInput() {
      this.showCustomerResults = true;
      if (!this.customerSearchQuery.trim()) {
        this.selectedCustomer = "";
        this.form.customerid = "";
        this.customerListFromApi = [];
        return;
      }
      clearTimeout(this.customerLookupTimer);
      this.customerLookupTimer = setTimeout(() => {
        this.fetchCustomerListFromApi(this.customerSearchQuery.trim());
      }, 250);
    },

    selectCustomer(customer) {
      this.selectedCustomer = customer.customerid;
      this.showCustomerResults = false;
    },

    getPrefillCustomerFromSearch() {
      const value = this.customerSearchQuery.trim();
      if (!value) {
        return {
          customername: this.form.customername || "",
          mobile: this.form.mobile || "",
        };
      }
      const isPhoneLike = /^[\d+\s()-]+$/.test(value);
      return {
        customername: this.form.customername || (isPhoneLike ? "" : value),
        mobile: this.form.mobile || (isPhoneLike ? value : ""),
      };
    },

    openCustomerModal() {
      const prefill = this.getPrefillCustomerFromSearch();
      this.customerModalForm = {
        customername: prefill.customername,
        mobile: prefill.mobile,
        gstno: this.form.gstno || "",
        address: this.form.address || "",
      };
      this.customerModalPhoneSubmitted = false;
      this.showCustomerModal = true;
      this.$nextTick(() => {
        const input = this.$refs.customerModalNameInput;
        if (input?.focus) input.focus();
      });
    },

    closeCustomerModal() {
      this.showCustomerModal = false;
      this.customerModalSaving = false;
      this.customerModalForm = this.getEmptyCustomerModalForm();
      this.customerModalPhoneSubmitted = false;
    },

    async createCustomerFromModal() {
      if (!this.customerModalForm.customername?.trim()) {
        Swal.fire({ icon: "warning", title: "Customer name is required" });
        return;
      }
      this.customerModalPhoneSubmitted = true;
      if (!this.$refs.customerModalMobileInput?.validate()) {
        Swal.fire({ icon: "warning", title: "Invalid mobile number" });
        return;
      }

      this.customerModalSaving = true;
      try {
        const payload = {
          customername: this.customerModalForm.customername.trim(),
          mobile: this.customerModalForm.mobile?.trim() || null,
          gstno: this.customerModalForm.gstno?.trim() || null,
          address: this.customerModalForm.address?.trim() || null,
        };
        const response = await axios.post("/v1/customers", payload);
        const customer = response.data;
        this.customerListFromApi = [
          { ...customer, customerlabel: this.buildCustomerLabel(customer) },
          ...this.customerListFromApi.filter((entry) => entry.customerid !== customer.customerid),
        ];
        this.selectedCustomer = customer.customerid;
        this.closeCustomerModal();
        Swal.fire({ icon: "success", title: "Customer created", timer: 1400, showConfirmButton: false });
      } catch (error) {
        this.customerModalSaving = false;
        Swal.fire(error.response?.data?.detail || "Failed to create customer.");
      }
    },

    async fetchItemListFromApi(category = "", search = "") {
      try {
        const params = {};
        if (category) params.category = category;
        if (search) params.search = search;
        params.stocked_only = true;
        const res = await axios.get("/v1/sales/items", { params });
        const data = Array.isArray(res.data) ? res.data : res.data.items ?? [];
        this.allItems = data;
        this.filteredItems = data;
      } catch (error) {
        console.error("Error fetching items:", error);
        this.allItems = [];
        this.filteredItems = [];
      }
    },

    async fetchCustomerListFromApi(search = "") {
      const params = { limit: 10 };
      if (search) params.search = search;
      const response = await axios.get("/v1/customers", { params });
      this.customerListFromApi = response.data.map((customer) => ({
        ...customer,
        customerlabel: this.buildCustomerLabel(customer),
      }));
    },

    async fetchCategoryListFromApi() {
      try {
        const response = await axios.get("/v1/category");
        this.categoriesFromApi = response.data || [];
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },

    resetItemSelection(item) {
      item.itemid = "";
      item.itemname = "";
      item.rate = 0;
      item.purchaseprice = 0;
      item.discountamt = 0;
      item.discountvalue = 0;
      item.quantity = 0;
      item.freequantity = 0;
      item.amount = 0;
      item.taxtype = "GST";
      item.taxpercent = 0;
      item.taxablevalue = 0;
      item.taxamount = 0;
      item.cgst = 0;
      item.sgst = 0;
      item.igst = 0;
      item.istaxinclusive = false;
      item.batchno = "";
      item.expirydate = "";
      item.iteminfo = null;
      item._lastLoadedItemId = "";
      item._layerRequestId = 0;
      item.selectedPurchaseItemId = null;
      item.layerOptions = [];
      item.allLayers = [];
      item.layerMap = {};
      item.layerRemainingQty = 0;
    },

    onItemCategoryChange(index) {
      const item = this.form.items[index];
      this.resetItemSelection(item);
      if (this.openDropdownIndex === index) this.closeDropdown();
      this.computeTotals();
    },

    clearItemSelection(index) {
      const item = this.form.items[index];
      if (!item) return;
      const previousItemId = item.itemid;
      this.resetItemSelection(item);
      this.refreshLayerOptionsForItem(previousItemId);
      this.computeTotals();
      this.setActiveRow(index);
      this.$nextTick(() => this.focusRowItemSearch(index));
    },

    hydrateLayerDetails(item, data) {
      const rawInclusive = data.istaxinclusive ?? data.is_tax_inclusive ?? false;
      const isTaxInclusive = rawInclusive === true || rawInclusive === 1 || rawInclusive === "1" || rawInclusive === "true";
      const mrp = Number(data.mrp ?? data.unitprice ?? 0);
      item.selectedPurchaseItemId = data.purchaseitemid ?? item.selectedPurchaseItemId;
      item.rate = mrp || Number(data.unitprice ?? 0);
      item.purchaseprice = Number(data.purchaseprice ?? data.purchaserate ?? 0);
      item.taxpercent = Number(data.taxpercent ?? 0);
      item.taxtype = data.taxtype ?? data.tax_type ?? "GST";
      item.istaxinclusive = mrp > 0 ? true : isTaxInclusive;
      item.batchno = data.batchno ?? "";
      item.expirydate = data.expirydate ?? "";
      item.discountamt = 0;
      item.layerRemainingQty = parseFloat(data.remainingqty ?? data.available ?? 0);
      item.quantity = item.layerRemainingQty > 0 ? 1 : 0;
      item.freequantity = 0;
      item.iteminfo = {
        purchaserate: Number(data.purchaseprice ?? data.purchaserate ?? 0),
        mrp: Number(data.mrp ?? 0),
        wholesalerate: Number(data.wholesalerate ?? 0),
        available: Number(data.totalavailable ?? data.available ?? 0),
      };
      item._lastLoadedItemId = data.itemid;
      this.calculateItemTotal(item);
    },

    _applyNoLayers(item) {
      item.selectedPurchaseItemId = null;
      item.layerRemainingQty = 0;
      item.rate = 0;
      item.purchaseprice = 0;
      item.taxpercent = 0;
      item.batchno = "";
      item.expirydate = "";
      item.quantity = 0;
      item.freequantity = 0;
      item.discountamt = 0;
      item.discountvalue = 0;
      item.iteminfo = { purchaserate: 0, mrp: 0, wholesalerate: 0, available: 0 };
      this.calculateItemTotal(item);
    },

    buildLayerOptionText(layer) {
      return `${layer.billno || layer.purchaseid} | Rem ${layer.remainingqty} | Batch ${layer.batchno || "-"} | Cost ${layer.purchaseprice ?? 0}`;
    },

    refreshLayerOptionsForItem(itemid) {
      if (!itemid) return;

      const rows = this.form.items.filter((row) => row.itemid === itemid);
      const used = new Set();

      rows.forEach((row) => {
        const usedBefore = new Set(used);
        const allLayers = Array.isArray(row.allLayers) ? row.allLayers : [];
        const current = row.selectedPurchaseItemId != null ? String(row.selectedPurchaseItemId) : null;

        if (current && usedBefore.has(current)) {
          row.selectedPurchaseItemId = null;
        }

        if (row.selectedPurchaseItemId == null) {
          const next = allLayers.find((layer) => !usedBefore.has(String(layer.purchaseitemid)));
          if (next) {
            row.selectedPurchaseItemId = next.purchaseitemid;
            this.hydrateLayerDetails(row, next);
          } else {
            row.layerOptions = [];
            this._applyNoLayers(row);
            return;
          }
        }

        const finalSelection = String(row.selectedPurchaseItemId);
        row.layerOptions = allLayers
          .filter((layer) => {
            const layerId = String(layer.purchaseitemid);
            return layerId === finalSelection || !usedBefore.has(layerId);
          })
          .map((layer) => ({
            value: layer.purchaseitemid,
            text: this.buildLayerOptionText(layer),
          }));

        used.add(finalSelection);
      });
    },

    async fetchPurchaseLayers(item) {
      const requestId = (item._layerRequestId || 0) + 1;
      item._layerRequestId = requestId;
      item.selectedPurchaseItemId = null;
      item.layerOptions = [];
      item.allLayers = [];
      item.layerMap = {};
      item.layerRemainingQty = 0;

      const res = await axios.get(`/v1/sales/purchase-layers/${item.itemid}`, {
        params: {
          locationid: this.getLocationId() || undefined,
        },
      });
      if (item._layerRequestId !== requestId) return;

      const layers = Array.isArray(res.data) ? res.data : [];
      item.allLayers = layers;
      item.layerMap = layers.reduce((accumulator, layer) => {
        accumulator[String(layer.purchaseitemid)] = layer;
        return accumulator;
      }, {});

      if (!layers.length) {
        this._applyNoLayers(item);
        return;
      }

      this.refreshLayerOptionsForItem(item.itemid);
    },

    onLayerChange(item, selectedPurchaseItemId) {
      item.selectedPurchaseItemId = selectedPurchaseItemId;
      const layer = item.layerMap?.[String(selectedPurchaseItemId)];
      if (!layer) return;
      this.hydrateLayerDetails(item, layer);
      this.refreshLayerOptionsForItem(item.itemid);
    },

    async fetchShopStateCode() {
      try {
        const res = await axios.get("/v1/company-settings/tax-info");
        this.shopStateCode = res.data?.shop_state_code || null;
      } catch (error) {
        console.error("Error fetching shop state code:", error);
      }
    },

    calculateItemTotal(item) {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      const taxPercent = parseFloat(item.taxpercent) || 0;
      const discountPercent = parseFloat(item.discountamt) || 0;
      const gross = qty * rate;
      const discountValue = (gross * discountPercent) / 100;
      const discountedGross = Math.max(gross - discountValue, 0);

      item.discountvalue = parseFloat(discountValue.toFixed(2));

      if (item.taxtype === "EXEMPT" || item.taxtype === "NONGST") {
        item.taxablevalue = parseFloat(discountedGross.toFixed(2));
        item.cgst = 0;
        item.sgst = 0;
        item.igst = 0;
        item.taxamount = 0;
        item.amount = parseFloat(discountedGross.toFixed(2));
        this.computeTotals();
        return;
      }

      let taxableValue = 0;
      let taxAmount = 0;
      if (item.istaxinclusive) {
        taxableValue = parseFloat((discountedGross / (1 + taxPercent / 100)).toFixed(2));
        taxAmount = parseFloat((discountedGross - taxableValue).toFixed(2));
      } else {
        taxableValue = parseFloat(discountedGross.toFixed(2));
        taxAmount = parseFloat(((taxableValue * taxPercent) / 100).toFixed(2));
      }

      if (this.isInterState) {
        item.cgst = 0;
        item.sgst = 0;
        item.igst = taxAmount;
      } else {
        item.cgst = parseFloat((taxAmount / 2).toFixed(2));
        item.sgst = parseFloat((taxAmount / 2).toFixed(2));
        item.igst = 0;
      }

      item.taxablevalue = taxableValue;
      item.taxamount = taxAmount;
      item.amount = item.istaxinclusive ? parseFloat(discountedGross.toFixed(2)) : taxableValue;
      this.computeTotals();
    },

    computeTotals() {
      let totalAmount = 0;
      let totalDiscount = 0;
      let totalTax = 0;
      let taxableValue = 0;
      let cgst = 0;
      let sgst = 0;
      let igst = 0;

      this.form.items.forEach((item) => {
        const lineAmount = parseFloat(item.amount) || 0;
        const lineTax = parseFloat(item.taxamount) || 0;
        const lineTotal = item.istaxinclusive ? lineAmount : lineAmount + lineTax;
        totalAmount += lineTotal;
        totalDiscount += parseFloat(item.discountvalue) || 0;
        totalTax += lineTax;
        taxableValue += parseFloat(item.taxablevalue) || 0;
        cgst += parseFloat(item.cgst) || 0;
        sgst += parseFloat(item.sgst) || 0;
        igst += parseFloat(item.igst) || 0;
      });

      this.totals.totalAmount = parseFloat(totalAmount.toFixed(2));
      this.totals.taxableValue = parseFloat(taxableValue.toFixed(2));
      this.totals.cgst = parseFloat(cgst.toFixed(2));
      this.totals.sgst = parseFloat(sgst.toFixed(2));
      this.totals.igst = parseFloat(igst.toFixed(2));
      this.totals.totalDiscount = parseFloat(totalDiscount.toFixed(2));
      this.totals.totalTax = parseFloat(totalTax.toFixed(2));
      this.totals.netAmount = parseFloat(
        (totalAmount + (parseFloat(this.form.roundoff) || 0)).toFixed(2)
      );
      if (this.isCreditSale && (parseFloat(this.form.paid_amount) || 0) > this.totals.netAmount) {
        this.form.paid_amount = this.totals.netAmount;
      }
    },

    addItem(options = {}) {
      const { focus = false } = options;
      this.form.items.push(this.newItemRow());
      this.activeRow = this.form.items.length - 1;
      this.computeTotals();
      if (focus) {
        this.$nextTick(() => this.focusRowItemSearch(this.activeRow));
      }
    },

    addItemAndFocus() {
      this.addItem({ focus: true });
    },

    removeItem(index) {
      const removedItemId = this.form.items[index]?.itemid;
      if (this.form.items.length === 1) {
        this.clearItemSelection(index);
        return;
      }
      this.form.items.splice(index, 1);
      this.refreshLayerOptionsForItem(removedItemId);
      this.activeRow = Math.max(0, Math.min(this.activeRow, this.form.items.length - 1));
      this.computeTotals();
    },

    clearForm() {
      this.selectedCustomer = "";
      this.customerSearchQuery = "";
      this.quickItemSearch = "";
      this.moreCustomerDetailsOpen = false;
      this.showCustomerResults = false;
      this.openDropdownIndex = null;
      this.activeRow = 0;
      this.salePhoneSubmitted = false;
      this.form = this.getInitialForm();
      this.totals = {
        totalAmount: 0,
        taxableValue: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalDiscount: 0,
        totalTax: 0,
        netAmount: 0,
      };
      this.fetchNextBillNo();
    },

    validateForm() {
      this.salePhoneSubmitted = true;
      if (!this.$refs.saleMobileInput?.validate()) {
        Swal.fire("Please enter a valid mobile number");
        return false;
      }
      if (!this.form.billdate) {
        Swal.fire("Bill date is required");
        return false;
      }
      if (!this.form.items.length) {
        Swal.fire("Add at least one item");
        return false;
      }
      for (let index = 0; index < this.form.items.length; index += 1) {
        const item = this.form.items[index];
        if (!item.itemid) {
          Swal.fire(`Select item for row ${index + 1}`);
          return false;
        }
        if (!(parseFloat(item.quantity) > 0)) {
          Swal.fire(`Quantity must be greater than 0 for row ${index + 1}`);
          return false;
        }
      }
      if (this.isCreditSale) {
        if (!this.form.customerid) {
          Swal.fire("Select or create a saved customer for credit sales");
          return false;
        }
        const paidAmount = parseFloat(this.form.paid_amount) || 0;
        if (paidAmount < 0) {
          Swal.fire("Paid amount cannot be negative");
          return false;
        }
        if (paidAmount > this.totals.netAmount) {
          Swal.fire("Paid amount cannot exceed net amount");
          return false;
        }
      }
      return true;
    },

    async submitSales() {
      if (!this.validateForm()) return;
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to save this sale?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, save it!",
      });
      if (!result.isConfirmed) return;

      const payload = {
        billno: null,
        billdate: this.form.billdate,
        locationid: this.getLocationId() || null,
        customerid: this.form.customerid || null,
        customername: this.form.customername || null,
        mobile: this.form.mobile || null,
        isinterstate: this.isInterState,
        customergstin: this.form.gstno || null,
        placeofsupply: this.isInterState ? (this.form.gstno?.substring(0, 2) || null) : this.shopStateCode,
        billtype: this.isInterState ? (this.form.gstno ? "B2B" : "B2C") : "B2C",
        totalamount: this.totals.totalAmount,
        discountamount: this.totals.totalDiscount,
        taxamount: this.totals.totalTax,
        roundoff: parseFloat(this.form.roundoff) || 0,
        netamount: this.totals.netAmount,
        paymentmode: this.form.paymentmode || null,
        is_credit: this.isCreditSale,
        due_date: this.isCreditSale ? (this.form.due_date || null) : null,
        paid_amount: this.isCreditSale ? (parseFloat(this.form.paid_amount) || 0) : this.totals.netAmount,
        remarks: this.form.remarks || null,
        items: this.form.items.map((item) => ({
          itemid: item.itemid,
          locationid: this.getLocationId() || null,
          purchaseitemid: item.selectedPurchaseItemId || null,
          quantity: parseFloat(item.quantity) || 0,
          rate: parseFloat(item.rate) || 0,
          taxtype: item.taxtype || "GST",
          taxpercent: parseFloat(item.taxpercent) || 0,
          taxablevalue: parseFloat(item.taxablevalue) || 0,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          igst: parseFloat(item.igst) || 0,
          istaxinclusive: item.istaxinclusive || false,
          discount: parseFloat(item.discountamt) || 0,
          taxamount: parseFloat(item.taxamount) || 0,
          amount: parseFloat(item.amount) || 0,
          batchno: item.batchno || null,
          expirydate: item.expirydate || null,
          freequantity: parseFloat(item.freequantity) || 0,
        })),
      };

      axios
        .post("/v1/sales", payload)
        .then(() => {
          Swal.fire("Sale saved successfully");
          this.$router.push({ path: "/saleslist" });
        })
        .catch((error) => {
          Swal.fire(error.response?.data?.detail || "An error occurred while saving the sale.");
        });
    },
  },
};
</script>

<style scoped>
.sales-pos-card {
  border: 1px solid #d9dee7;
  border-radius: 16px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
  background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.sales-pos-body {
  padding: 16px !important;
}

.sales-pos-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pos-utility-bar,
.customer-strip,
.items-panel,
.notes-card,
.totals-card {
  border: 1px solid #dde3ec;
  border-radius: 12px;
  background: #ffffff;
}

.pos-utility-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 14px;
  padding: 12px;
  background: linear-gradient(135deg, #f7fafc 0%, #ffffff 72%);
}

.pos-shortcuts,
.pos-utility-fields,
.lookup-control-row,
.customer-strip-head,
.items-panel-head,
.items-head-actions,
.sticky-footer-actions,
.modal-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pos-shortcuts,
.pos-utility-fields,
.items-head-actions {
  flex-wrap: wrap;
}

.pos-chip,
.active-row-badge,
.summary-count-pill,
.notes-hint-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.pos-chip {
  border: 1px solid #dbe3f0;
  background: #ffffff;
  color: #415166;
}

.compact-field {
  min-width: 0;
}

.compact-field-search {
  min-width: 320px;
  flex: 1 1 420px;
}

.compact-field-mode {
  width: 160px;
}

.compact-label {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 0.73rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.compact-input,
.table-input,
.table-select,
.totals-input,
.item-dd-search,
.item-dd-trigger {
  min-height: 34px;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #0f172a;
  background: #ffffff;
}

.compact-input:focus,
.table-input:focus,
.table-select:focus,
.totals-input:focus,
.item-dd-search:focus,
.item-dd-trigger:focus {
  border-color: #2c7be5;
  box-shadow: 0 0 0 3px rgba(44, 123, 229, 0.12);
  outline: none;
}

.compact-readonly {
  background: #f8fafc;
  color: #475569;
}

.compact-textarea {
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  font-size: 0.88rem;
  color: #0f172a;
  background: #ffffff;
  padding: 10px 12px;
}

.quick-scan-input {
  border-style: dashed;
}

.customer-strip {
  padding: 12px;
}

.customer-strip-head,
.items-panel-head,
.notes-card-head,
.totals-card-head {
  justify-content: space-between;
  margin-bottom: 10px;
}

.strip-title {
  margin: 0;
  color: #172033;
  font-size: 0.96rem;
  font-weight: 700;
}

.strip-subtitle {
  margin: 3px 0 0;
  color: #7b8797;
  font-size: 0.78rem;
}

.customer-more-toggle,
.primary-row-btn,
.inline-add-btn,
.footer-btn {
  border: 0;
  cursor: pointer;
  transition: all 0.18s ease;
}

.customer-more-toggle {
  padding: 7px 12px;
  border-radius: 8px;
  background: #eef5ff;
  color: #1d4ed8;
  font-size: 0.8rem;
  font-weight: 700;
}

.customer-strip-grid {
  display: grid;
  grid-template-columns: minmax(320px, 2.1fr) repeat(3, minmax(150px, 0.8fr));
  gap: 10px;
  align-items: end;
}

.customer-main-field {
  min-width: 0;
}

.lookup-box {
  position: relative;
  flex: 1 1 auto;
}

.lookup-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 30;
  background: #ffffff;
  border: 1px solid #d7e2f0;
  border-radius: 10px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.14);
  max-height: 280px;
  overflow-y: auto;
}

.lookup-result-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 9px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lookup-result-item:hover {
  background: #f3f8ff;
}

.lookup-result-title {
  color: #111827;
  font-weight: 600;
}

.lookup-result-meta,
.lookup-empty-state {
  color: #6b7280;
  font-size: 0.82rem;
}

.lookup-empty-state {
  padding: 12px;
}

.inline-add-btn {
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #bfd6f7;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.inline-add-btn:hover,
.customer-more-toggle:hover {
  filter: brightness(0.98);
}

.customer-more-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef2f7;
}

.customer-address-field {
  grid-column: span 1;
}

.tax-state-row {
  margin-top: -4px;
}

.interstate-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.interstate-igst {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fdba74;
}

.interstate-intra {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #6ee7b7;
}

.items-panel {
  padding: 12px;
  min-width: 0;
}

.active-row-badge {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #d8e0ea;
}

.primary-row-btn {
  min-height: 36px;
  padding: 0 14px;
  border-radius: 9px;
  background: #1d4ed8;
  color: #ffffff;
  font-size: 0.84rem;
  font-weight: 700;
}

.primary-row-btn:hover,
.footer-btn-primary:hover {
  filter: brightness(1.05);
}

.pos-table-wrap {
  overflow-x: auto;
  overflow-y: visible;
  border: 1px solid #dfe5ee;
  border-radius: 12px;
  background: #ffffff;
  width: 100%;
  max-width: 100%;
}

.pos-table {
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;
  table-layout: fixed;
}

.pos-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 8px 10px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.pos-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #edf2f7;
  vertical-align: top;
}

.pos-row:hover {
  background: #fafcff;
}

.pos-row-active {
  background: #f5f9ff;
}

.pos-row-active .cell-num {
  color: #1d4ed8;
}

.col-num {
  width: 42px;
}

.col-item {
  width: 260px;
}

.col-layer {
  width: 210px;
}

.col-qty,
.col-rate,
.col-disc,
.col-gst,
.col-taxable,
.col-total {
  width: 92px;
}

.col-remove {
  width: 42px;
}

.cell-num {
  text-align: center;
  font-size: 0.82rem;
  font-weight: 700;
  color: #94a3b8;
}

.item-search-cell {
  position: relative;
}

.item-dd-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  user-select: none;
}

.item-dd-trigger-large {
  min-height: 38px;
  border-width: 1.5px;
}

.item-dd-placeholder {
  color: #94a3b8;
}

.item-dd-selected {
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-dd-arrow {
  color: #94a3b8;
  font-size: 0.7rem;
  margin-left: 10px;
}

.item-dd-portal {
  background: #ffffff;
  border: 1.5px solid #93c5fd;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.item-dd-search-wrap {
  padding: 8px;
  border-bottom: 1px solid #e9eef5;
}

.item-dd-search {
  width: 100%;
  padding: 0 10px;
}

.item-dd-list {
  max-height: 260px;
  overflow-y: auto;
}

.item-dd-option {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 9px 12px;
  cursor: pointer;
}

.item-dd-option:hover,
.item-dd-option-active {
  background: #eff6ff;
}

.item-option-name {
  color: #0f172a;
  font-size: 0.85rem;
  font-weight: 600;
}

.item-option-meta,
.item-dd-msg,
.qty-inline-note {
  color: #6b7280;
  font-size: 0.74rem;
}

.item-dd-msg {
  padding: 12px;
  text-align: center;
}

.table-input,
.table-select,
.totals-input {
  height: 34px;
  padding: 0 10px;
}

.qty-input {
  font-weight: 700;
}

.row-submeta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.row-submeta-tight {
  margin-top: 5px;
}

.row-meta-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2f7;
  color: #475569;
  font-size: 0.69rem;
  font-weight: 700;
}

.row-meta-soft {
  background: #eff6ff;
  color: #2563eb;
}

.row-meta-muted {
  background: #f8fafc;
  color: #64748b;
}

.row-meta-success {
  background: #ecfdf5;
  color: #047857;
}

.row-meta-warning {
  background: #fffbeb;
  color: #b45309;
}

.row-meta-danger {
  background: #fef2f2;
  color: #b91c1c;
}

.value-display {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.87rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.value-display-strong {
  background: #f0f9ff;
  color: #0f4c81;
  font-weight: 800;
}

.remove-row-btn {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
}

.remove-row-btn:hover {
  background: #fee2e2;
}

.sales-bottom-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 14px;
}

.notes-card,
.totals-card {
  padding: 12px;
}

.notes-textarea {
  min-height: 96px;
  resize: vertical;
}

.notes-hint-badge {
  background: #eef6ff;
  color: #1d4ed8;
}

.totals-panel {
  position: sticky;
  top: 88px;
  align-self: start;
}

.summary-count-pill {
  background: #f8fafc;
  color: #334155;
  border: 1px solid #dde5ef;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #edf2f7;
  color: #475569;
  font-size: 0.85rem;
}

.totals-row:last-child {
  border-bottom: none;
}

.totals-grand-row {
  margin-top: 4px;
  padding-top: 12px;
  border-top: 2px solid #dbeafe;
  color: #0f172a;
}

.totals-grand-row strong {
  color: #1d4ed8;
  font-size: 1.3rem;
  font-weight: 800;
}

.credit-payment-box {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.credit-status-chip {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 2px 10px;
  border-radius: 6px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 0.78rem;
  font-weight: 800;
}

.sticky-bill-footer {
  position: sticky;
  bottom: 0;
  z-index: 12;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 10px 18px;
  border-top: 1px solid #dbe3ec;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
}

.sticky-footer-summary {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.sticky-footer-label,
.sticky-footer-meta {
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
}

.sticky-footer-total {
  color: #0f172a;
  font-size: 1.45rem;
  font-weight: 800;
}

.footer-btn {
  min-height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 700;
}

.footer-btn-light {
  background: #f8fafc;
  color: #334155;
}

.footer-btn-muted {
  background: #e2e8f0;
  color: #334155;
}

.footer-btn-primary {
  background: #1d4ed8;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(29, 78, 216, 0.18);
}

.quick-create-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.quick-create-modal {
  width: min(100%, 680px);
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.22);
  padding: 22px;
}

.quick-create-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.quick-create-modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}

.quick-create-modal-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 0.88rem;
}

.quick-create-close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: #f3f4f6;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 1400px) {
  .customer-strip-grid {
    grid-template-columns: minmax(280px, 2fr) repeat(3, minmax(140px, 0.85fr));
  }

  .pos-table {
    min-width: 980px;
  }
}

@media (max-width: 1200px) {
  .pos-utility-bar,
  .customer-strip-head,
  .items-panel-head,
  .sticky-bill-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .customer-strip-grid,
  .customer-more-grid,
  .sales-bottom-grid {
    grid-template-columns: 1fr;
  }

  .totals-panel {
    position: static;
  }

  .sticky-footer-actions {
    width: 100%;
    justify-content: stretch;
  }

  .footer-btn {
    flex: 1 1 0;
  }
}
</style>

<style>
.sales-pos-card,
.sales-pos-card .card-body,
.pos-table-wrap,
.pos-row,
.cell-item {
  overflow: visible !important;
}
</style>
